import { getCompletedShipmentStats } from "@/lib/completed-shipments/server"
import type { ShipmentStats } from "@/lib/completed-shipments"
import { GetStaticPaths, GetStaticProps } from "next"
import { useRouter } from "next/router"
import { getDriverProfile } from "@/lib/drivers/fetch"
import { DriverProfile } from "@/lib/drivers/types"
import { getActiveDriverIds } from "@/lib/drivers/active-profiles"
import {
  getDriverTenureLabel,
  getDriverTenureSentence,
} from "@/lib/drivers/date-utils"
import PageNav from "@/storyblok/sections/PageNav"
import Footer from "@/storyblok/static/Footer"
import { ReviewsContextProvider } from "@/context/ReviewsContext"
import { DriverProfileContent } from "@/components/drivers/driver-profile-content"

interface DriverProfilePageProps {
  completedShipments: ShipmentStats | null
  profile: DriverProfile | null
  tenureLabel: string | null
  tenureSentence: string
}

const CACHE_TTL = 3600 // 1 hour in seconds

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const driverIds = await getActiveDriverIds()

    const paths = driverIds.map(({ ID }) => ({
      params: { id: ID.toString() },
    }))

    return {
      paths,
      fallback: true,
    }
  } catch (error) {
    console.error("[Build] Error in getStaticPaths:", error)

    throw error
  }
}

export const getStaticProps: GetStaticProps<DriverProfilePageProps> = async ({
  params,
}) => {
  try {
    const id = params?.id as string

    // Validate that id is a number and exists
    if (!id || isNaN(Number(id))) {
      return { notFound: true, revalidate: CACHE_TTL }
    }

    // Fetch active driver IDs to validate this ID
    const driverIds = await getActiveDriverIds()

    // Check if the ID exists in our valid driver IDs list
    const validId = driverIds.some(
      (driver) => driver.ID === id || driver.ID === Number(id).toString(),
    )

    if (!validId) {
      return {
        redirect: {
          destination: "/drivers",
          permanent: false,
        },
        // Revalidate so a cached redirect self-heals once the driver becomes
        // active. Without this, Next.js caches the redirect (s-maxage=1yr) and
        // never re-runs getStaticProps, leaving newly-active drivers stuck
        // redirecting until the next deploy.
        revalidate: CACHE_TTL,
      }
    }

    const profile = await getDriverProfile(id)

    // Handle missing profile or data
    if (!profile?.data?.displayName) {
      return { notFound: true, revalidate: CACHE_TTL }
    }

    // Serialize wall-clock tenure at ISR time so SSR HTML and the first client
    // render share one string (avoid #418 when hydrate crosses a year boundary).
    const tenureLabel = getDriverTenureLabel(profile.data.joinedAt)
    const tenureSentence = getDriverTenureSentence(profile.data.joinedAt)

    return {
      props: {
        completedShipments: await getCompletedShipmentStats(),
        profile,
        tenureLabel,
        tenureSentence,
      },
      revalidate: CACHE_TTL,
    }
  } catch (error) {
    console.error(
      `[Build] Error in getStaticProps for ID: ${params?.id}:`,
      error,
    )

    return { notFound: true, revalidate: CACHE_TTL }
  }
}

export default function DriverProfilePage({
  profile,
  tenureLabel,
  tenureSentence,
}: DriverProfilePageProps) {
  const router = useRouter()

  // Handle loading state
  if (router.isFallback) {
    return (
      <>
        <PageNav
          noBlok
          buttonLabel="Get Transportation Quotes"
          route="shipments/new"
        />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
        </div>
        <Footer noBlok />
      </>
    )
  }

  // getStaticProps already 404s invalid IDs on the server. next/navigation's
  // notFound() is App Router only and throws unhandled here, so just render
  // the not-found state.
  if (!profile || !profile.data) {
    return (
      <>
        <PageNav
          noBlok
          buttonLabel="Get Transportation Quotes"
          route="shipments/new"
        />
        <div className="flex items-center justify-center min-h-screen">
          <h1 className="text-2xl font-bold text-gray-800">Driver not found</h1>
        </div>
        <Footer noBlok />
      </>
    )
  }

  return (
    <ReviewsContextProvider>
      <DriverProfileContent
        profile={profile}
        tenureLabel={tenureLabel}
        tenureSentence={tenureSentence}
      />
    </ReviewsContextProvider>
  )
}
